import { useEffect, useState } from 'react';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { identityApiRef, configApiRef } from '@backstage/core-plugin-api';

export const ApplicationProjectsField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<any>) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);

  const [userProjectCodes, setUserProjectCodes] = useState<string[]>([]);
  const [userProjects, setUserProjects] = useState<any[]>([]);

  const [applications, setApplications] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  // Local states
  const [application, setApplication] = useState('');
  const [applicationRef, setApplicationRef] = useState('');
  const [applicationDivision, setApplicationDivision] = useState('');
  const [applicationOwnerRef, setApplicationOwnerRef] = useState('');
  const [_project, setProject] = useState('');
  const [projectRef, setProjectRef] = useState('');

  // Obtener projectCodes del usuario logueado (o impersonado)
  useEffect(() => {
    const fetchUserProjects = async () => {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;

      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }

      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;

      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser, '(from ApplicationProjectsField)');
      }

      // Call OpenFGA to get user's authorized projects
      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user: "user:" + effectiveUser,
          type: "project",
        }),
      });
      const data = await response.json();
      const projectCodesFromFGA = (data.objects ?? [])
        .map((p: string) => {
          const match = p.match(/^project:(prj\d{7})$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];
      console.log('Project codes from OpenFGA (ApplicationProjectsField):', projectCodesFromFGA);
      setUserProjectCodes(Array.from(new Set(projectCodesFromFGA)));
    };

    fetchUserProjects();
  }, [identityApi, catalogApi, config]);

  // Cargar SOLO los proyectos del usuario
  useEffect(() => {
    const fetchProjects = async () => {
      if (userProjectCodes.length === 0) {
        setUserProjects([]);
        return;
      }

      const resp = await catalogApi.getEntities({ filter: { kind: 'Project' } });

      const filtered = resp.items.filter(p =>
        userProjectCodes
          .map(c => c.toUpperCase())
          .includes(String(p.spec?.number ?? '').toUpperCase()),
      );

      // De los filtered sacar solo los que no tienen parent
      const filteredParents = filtered.filter(p => !p.spec?.parent);
      setUserProjects(filteredParents);
    };

    fetchProjects();
  }, [userProjectCodes, catalogApi]);


  // -----------------------------------------------------------------------------------
  // ✔️ 1. Sincronizar con formData cuando cambia (ESTO ERA EL PROBLEMA)
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;

    setApplication(formData.application || '');
    setApplicationRef(formData.applicationRef || '');
    setApplicationOwnerRef(formData.applicationOwnerRef || '');
    setProject(formData.project || '');
    setProjectRef(formData.projectRef || '');

    // ✅ SOLO si viene definido
    if (formData.applicationDivision !== undefined) {
      setApplicationDivision(formData.applicationDivision);
    } else {
      setApplicationDivision('');
    }
  }, [formData]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2. Cargar Aplicaciones del usuario
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const fetchApplications = async () => {
      const appRefs = userProjects.flatMap(p => p.spec?.applications ?? []);
      const uniqueRefs = Array.from(new Set(appRefs));

      if (uniqueRefs.length === 0) {
        setApplications([]);
        return;
      }

      const resp = await catalogApi.getEntitiesByRefs({
        entityRefs: uniqueRefs,
      });

      setApplications(resp.items.filter(Boolean));
    };

    fetchApplications();
  }, [userProjects, catalogApi]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2b. Asegurar que la aplicación pre-seleccionada desde la URL esté en la lista
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const ensurePreselectedApp = async () => {
      const preselectedRef = formData?.applicationRef;
      if (!preselectedRef) return;

      const alreadyLoaded = applications.some(
        a =>
          `application:${a.metadata.namespace}/${a.metadata.name}` ===
          preselectedRef,
      );
      if (alreadyLoaded) return;

      try {
        const entity = await catalogApi.getEntityByRef(preselectedRef);
        if (entity) {
          setApplications(prev => {
            const duplicate = prev.some(
              a =>
                `application:${a.metadata.namespace}/${a.metadata.name}` ===
                preselectedRef,
            );
            return duplicate ? prev : [...prev, entity];
          });
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch preselected application:', preselectedRef, err);
      }
    };

    ensurePreselectedApp();
    // applications is intentionally included so we re-check once the user's own apps arrive,
    // but the early-return on alreadyLoaded prevents an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, formData?.applicationRef, catalogApi]);

  // -----------------------------------------------------------------------------------
  // ✔️ 3. Cargar Proyectos de la aplicación seleccionada
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!applicationRef) {
      setProjects([]);
      return;
    }

    const loadProjects = async () => {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;
      const backendUrl = config.getString('backend.baseUrl');

      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }

      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;

      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser);
      }

      // Llama a OFGA para sacar las aplicaciones del usuario
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user: "user:" + effectiveUser,
          type: "project",
          relation: "can_view_details",
        }),
      });

      const data = await response.json();

      const allowedProjectNumbers = data.objects.map((ref: string) => ref.split(':')[1]);

      console.log('Allowed projects from FGA:', allowedProjectNumbers);
      console.log('All projects:', userProjects.map(p => p.spec?.number));

      const fromUserProjects = userProjects.filter(p => {
        const matchesApplication =
          Array.isArray(p.spec?.applications)
            ? p.spec.applications.includes(applicationRef)
            : [p.spec?.applications].filter(Boolean).includes(applicationRef);

        const hasAccess = allowedProjectNumbers.includes(p.spec?.number);

        return matchesApplication && hasAccess;
      });

      if (fromUserProjects.length > 0) {
        setProjects(fromUserProjects);
        return;
      }

      // Preselected app is outside the user's project list — query catalog directly
      try {
        const resp = await catalogApi.getEntities({ filter: { kind: 'Project' } });
        const matching = resp.items.filter(p => {
          const matchesApplication =
            Array.isArray(p.spec?.applications)
              ? p.spec.applications.includes(applicationRef)
              : [p.spec?.applications].filter(Boolean).includes(applicationRef);

          const hasAccess = allowedProjectNumbers.includes(p.spec?.number);

          return !p.spec?.parent && matchesApplication && hasAccess;
        });
        setProjects(matching);
      } catch (err) {
        console.warn('⚠️ Could not fetch projects for preselected application:', err);
        setProjects([]);
      }
    };

    loadProjects();
  }, [applicationRef, userProjects, catalogApi]);

  // -----------------------------------------------------------------------------------
  // ✔️ 4. Handle de selección de aplicación
  // -----------------------------------------------------------------------------------
  const handleApplicationChange = async (app: any) => {
    const ref = `application:${app.metadata.namespace}/${app.metadata.name}`;

    const number = app.spec.number || '';
    const owner = app.spec.owner || '';

    let divisionId: string | undefined = undefined;

    // ✅ SOLO si existe division
    if (app.spec?.division) {
      try {
        const company: any = await catalogApi.getEntityByRef(app.spec.division);

        if (company?.spec?.subDivisionOf) {
          const division: any = await catalogApi.getEntityByRef(
            company.spec.subDivisionOf,
          );

          divisionId = division?.spec?.company_id;
        }
      } catch (err) {
        console.warn('⚠️ Error resolving division:', err);
      }
    }

    setApplication(number);
    setApplicationRef(ref);
    setApplicationOwnerRef(owner);
    setApplicationDivision(divisionId || ''); // UI puede quedarse vacío

    // Reset project
    setProject('');
    setProjectRef('');

    // ✅ Construir dinámicamente el objeto
    const payload: any = {
      application: number,
      applicationRef: ref,
      applicationOwnerRef: owner,
      project: '',
      projectRef: '',
    };

    // ✅ SOLO añadir si existe
    if (divisionId) {
      payload.applicationDivision = divisionId;
    }

    onChange(payload);
  };

  // -----------------------------------------------------------------------------------
  // ✔️ 5. Handle de selección de proyecto
  // -----------------------------------------------------------------------------------
  const handleProjectChange = (proj: any) => {
    const ref = `project:${proj.metadata.namespace}/${proj.metadata.name}`;
    const number = proj.spec.number || '';

    setProject(number);
    setProjectRef(ref);

    onChange({
      application,
      applicationRef,
      applicationOwnerRef,
      applicationDivision,
      project: number,
      projectRef: ref,
    });
  };

  // -----------------------------------------------------------------------------------
  // ✔️ Render
  // -----------------------------------------------------------------------------------
  return (
    <FormControl margin="normal" required={required} fullWidth>
      {/* Application */}
      <Autocomplete
        options={applications}
        getOptionLabel={opt => opt.metadata.title || ''}
        value={
          applications.find(
            a => `application:${a.metadata.namespace}/${a.metadata.name}` === applicationRef,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) handleApplicationChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Application"
            required
            helperText={rawErrors?.[0] || 'Search for an Application'}
          />
        )}
      />

      {/* Project */}
      <Autocomplete
        options={projects}
        getOptionLabel={opt => opt.metadata.title || ''}
        value={
          projects.find(
            p => `project:${p.metadata.namespace}/${p.metadata.name}` === projectRef,
          ) || null
        }
        disabled={!applicationRef}
        onChange={(_, newValue) => {
          if (newValue) handleProjectChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Project"
            required
            helperText={
              !applicationRef
                ? 'Select an Application first'
                : rawErrors?.[0] || 'Search for a Project'
            }
          />
        )}
      />
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// ✔️ Validación
// -----------------------------------------------------------------------------------
export const applicationProjectsCaseValidation = (
  value: { application?: string; project?: string },
  validation: FieldValidation,
) => {
  if (!value?.application) validation.addError('Application is required.');
  if (!value?.project) validation.addError('Project is required.');
};

// -----------------------------------------------------------------------------------
// ✔️ Registro del Custom Field
// -----------------------------------------------------------------------------------
export const ApplicationProjectsCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationProjectsField',
    component: ApplicationProjectsField,
    validation: applicationProjectsCaseValidation,
  }),
);
